import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import { Popover } from '@/shared/ui/Popover';
import { formFieldSizeVariants } from '../helpers/field-size';
import { selectTriggerStyles } from '../select/helpers/trigger-styles';
import { CascaderPopoverContent } from './components/CascaderPopoverContent';
import { CascaderTrigger } from './components/CascaderTrigger';
import type { CascaderProps } from './helpers';
import { useCascader } from './hooks/useCascader';
import { SearchableMultipleCascader } from './SearchableMultipleCascader';
import { SearchableSingleCascader } from './SearchableSingleCascader';

/**
 * Cascader コンポーネント
 *
 * Ant Design の Cascader をベースにした、
 * shadcn/ui スタイルのカスケード選択コンポーネント
 */
export const Cascader = ({
  id,
  options,
  value = [],
  onChange,
  placeholder = '選択してください',
  disabled = false,
  hasError = false,
  className,
  showSearch = false,
  changeOnSelect = false,
  displayRender,
  multiple = false,
  showCheckedStrategy,
  maxTagCount,
  size = 'sm',
  disabledItemValues = [],
  disabledItemReasons,
  showAllClear = false,
  onlyLeafSelectable = true,
}: CascaderProps) => {
  const {
    // 状態
    open,
    searchValue,
    activePath,
    selectedPaths,
    hoveredIndex,
    activeIndex,
    inputRef,

    // 計算値
    processedOptions,
    hasValue,
    displayText,
    visibleTags,
    remainingCount,
    columnCount,
    searchResults,
    isSearching,

    // セッター
    setSearchValue,

    // ハンドラー
    handleOpenChange,
    handleRemoveTag,
    handleClear,
    handleCheckboxToggle,
    handleNavigate,
    handleSelectSearchResult,
    handleSearchKeyDown,
    handleKeyDown,
    handleMouseEnter,
    handleMouseLeave,
  } = useCascader({
    options,
    value,
    onChange,
    showSearch,
    changeOnSelect,
    displayRender,
    multiple,
    showCheckedStrategy,
    maxTagCount,
    disabledItemValues,
    disabledItemReasons,
    onlyLeafSelectable,
  });

  const shouldShowClearButton = showAllClear && hasValue && !disabled;

  const listboxId = id ? `${id}-search-listbox` : undefined;
  const activeOptionId = activeIndex >= 0 && id ? `${id}-search-option-${activeIndex}` : undefined;

  const popoverContent = (
    <CascaderPopoverContent
      isSearching={isSearching}
      searchResults={searchResults}
      selectedPaths={selectedPaths}
      multiple={multiple}
      onlyLeafSelectable={onlyLeafSelectable}
      onSelectSearchResult={handleSelectSearchResult}
      processedOptions={processedOptions}
      activePath={activePath}
      hoveredIndex={hoveredIndex}
      columnCount={columnCount}
      onCheckboxToggle={handleCheckboxToggle}
      onNavigate={handleNavigate}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      activeIndex={activeIndex}
      id={id}
    />
  );

  // showSearch 時は combobox パターン
  if (showSearch) {
    if (multiple) {
      return (
        <SearchableMultipleCascader
          id={id}
          open={open}
          onOpenChange={handleOpenChange}
          disabled={disabled}
          hasError={hasError}
          className={className}
          size={size}
          shouldShowClearButton={shouldShowClearButton}
          onClear={handleClear}
          inputRef={inputRef}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          onSearchKeyDown={handleSearchKeyDown}
          placeholder={placeholder}
          listboxId={listboxId}
          activeOptionId={activeOptionId}
          visibleTags={visibleTags}
          remainingCount={remainingCount}
          processedOptions={processedOptions}
          selectedPaths={selectedPaths}
          onRemoveTag={handleRemoveTag}
          popoverContent={popoverContent}
        />
      );
    }

    return (
      <SearchableSingleCascader
        id={id}
        open={open}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        hasError={hasError}
        className={className}
        size={size}
        shouldShowClearButton={shouldShowClearButton}
        onClear={handleClear}
        inputRef={inputRef}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        onSearchKeyDown={handleSearchKeyDown}
        placeholder={placeholder}
        displayText={displayText}
        listboxId={listboxId}
        activeOptionId={activeOptionId}
        popoverContent={popoverContent}
      />
    );
  }

  // showSearch=false 時は従来のボタントリガー
  return (
    <div className={cn('relative w-full', className)}>
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        contentClassName="w-auto p-2"
        contentAlign="start"
        onKeyDown={handleKeyDown}
        content={popoverContent}
      >
        <Button
          id={id}
          type="button"
          variant="non-outline"
          size="none"
          disabled={disabled}
          className={cn(
            multiple && selectedPaths.length > 0
              ? selectTriggerStyles({ height: 'flexible' })
              : selectTriggerStyles({ height: 'fixed' }),
            'flex w-full items-center justify-between',
            'font-normal',
            'ring-offset-background',
            'placeholder:text-text-muted',
            'disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-100',
            formFieldSizeVariants({ size }),
            shouldShowClearButton && 'pr-10',
          )}
          aria-invalid={hasError || undefined}
          aria-expanded={open}
        >
          <CascaderTrigger
            multiple={multiple}
            displayText={displayText}
            placeholder={placeholder}
            visibleTags={visibleTags}
            remainingCount={remainingCount}
            options={processedOptions}
            onRemoveTag={handleRemoveTag}
            shouldShowClearButton={shouldShowClearButton}
          />
        </Button>
      </Popover>
      {shouldShowClearButton && (
        <Button
          variant="ghost"
          size="none"
          onClick={handleClear}
          className="absolute top-1/2 right-3 -translate-y-1/2"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="text-text-muted" />
        </Button>
      )}
    </div>
  );
};
